import PageLayout from "components/PageLayout";
import { useCategories } from "hooks/categories";
import { useStudentGroups } from "hooks/students";
import { Assessment } from "types/assessments";

import TextField from "components/Form/TextField";
import { Box, MenuItem } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Button from "components/button/Button";
import { useState } from "react";

interface IAssementFormProps {
  initialAssessment?: Assessment;
  inEditMode?: boolean;
  onSubmit: (assesement: Partial<Assessment>) => Promise<void | any>;
}

const validator = Yup.object().shape({
  name: Yup.string()
    .required("Enter assessment name")
    .min(3, "Three ore more characters allowed"),
  description: Yup.string(),
  category_id: Yup.number().required("Please select subject"),
  group_id: Yup.number().required("Please select group"),
  number_of_question: Yup.number().required(
    "Please enter the number of quesitons for this assessment"
  ),
  pick_format: Yup.string().required(
    "Please select the right question picking format"
  ),
  time_in_mins: Yup.number().required(
    "Enter total number of allowed minutes for the assessment"
  ),
});

const AssessmentForm = ({
  initialAssessment,
  onSubmit,
}: IAssementFormProps) => {
  const {
    isLoading: isGroupsLoading,
    isFetching: isGroupsFetching,
    error: groupsError,
    data: groups,
    refetch: refetchGroups,
  } = useStudentGroups();
  const {
    isLoading: isCategoryLoading,
    isFetching: isCategoryFetching,
    error: categoryError,
    data: categories,
    refetch,
  } = useCategories();

  const [selectedGroup, setSelectedGroup] = useState("");

  const filterCategories = () => {
    if(!selectedGroup) return categories?.data

    // get the group record for the group id.
    const selectedGroupRecord = groups?.data.filter((items) => items.id === parseInt(selectedGroup));
    
    // filter the category to show only matching level code with group
    return categories?.data.filter((items) => items.level_code === selectedGroupRecord?.[0].level_code)
  }

  const retry = () => {
    refetch();
    refetchGroups();
  };

  const isLoading =
    isGroupsLoading ||
    isGroupsFetching ||
    isCategoryFetching ||
    isCategoryLoading;

  return (
    <PageLayout
      loading={isLoading}
      error={groupsError || categoryError}
      onRetry={() => retry()}
    >
      <Formik
        initialValues={{
          name: initialAssessment?.name,
          description: initialAssessment?.description,
          group_id: initialAssessment?.group_id,
          category_id: initialAssessment?.category_id,
          time_in_mins: initialAssessment?.time_in_mins,
          pick_format: initialAssessment?.pick_format,
          number_of_question: initialAssessment?.number_of_question,
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          await onSubmit(values);
          setSubmitting(false);
        }}
        validationSchema={validator}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form
            style={{ textAlign: "center", marginTop: 12, marginBottom: 24 }}
          >
            <TextField
              type="text"
              name="name"
              error={!!(errors.name && touched.name)}
              helperText={errors.name}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              value={values.name || ""}
              label="Assessment Name"
              variant="outlined"
              fullWidth
              style={{ marginBottom: 24 }}
            />

            <TextField
              type="text"
              name="description"
              error={!!(errors.description && touched.description)}
              helperText={errors.description}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.description || ""}
              label="Assessment Description"
              variant="outlined"
              fullWidth
              style={{ marginBottom: 24 }}
            />

            <TextField
              type="text"
              name="group_id"
              error={!!(errors.group_id && touched.group_id)}
              helperText={errors.group_id}
              onChange={(event) => {
                setSelectedGroup(event.target.value)
                values.category_id = undefined;
                handleChange(event) 
              }}
              onBlur={handleBlur}
              required
              value={values.group_id || ""}
              label="Student Group"
              variant="outlined"
              fullWidth
              select
              placeholder="Select group of students to target"
              style={{ marginBottom: 24, textAlign: "left" }}
            >
              {(groups?.data || []).map((group) => {
                return (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name?.toUpperCase()}
                  </MenuItem>
                );
              })}
            </TextField>

            <TextField
              type="text"
              name="category_id"
              error={!!(errors.category_id && touched.category_id)}
              helperText={errors.category_id}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              value={values.category_id || ""}
              label="Subject"
              variant="outlined"
              fullWidth
              select
              placeholder="Select subject to use for this assessment"
              style={{ marginBottom: 24, textAlign: "left" }}
            >
              {(filterCategories() || []).map((category) => {
                return (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name?.toUpperCase()}
                  </MenuItem>
                );
              })}
            </TextField>

            <TextField
              type="number"
              name="number_of_question"
              error={
                !!(errors.number_of_question && touched.number_of_question)
              }
              helperText={errors.number_of_question}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              value={values.number_of_question || ""}
              label="Number of questions"
              variant="outlined"
              placeholder="Number of questions for this assessment"
              fullWidth
              style={{ marginBottom: 24 }}
            />
            <TextField
              type="text"
              name="pick_format"
              error={!!(errors.pick_format && touched.pick_format)}
              helperText={errors.pick_format}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              value={values.pick_format || ""}
              label="Select Assessment Question"
              variant="outlined"
              fullWidth
              select
              placeholder="How the assessment question are selected"
              style={{ marginBottom: 24, textAlign: "left" }}
            >
              <MenuItem key="latest" value="latest">
                Latest Questions
              </MenuItem>
              <MenuItem key="random" value="random">
                Random Questions
              </MenuItem>
            </TextField>
            <TextField
              type="number"
              name="time_in_mins"
              error={!!(errors.time_in_mins && touched.time_in_mins)}
              helperText={errors.time_in_mins}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              value={values.time_in_mins || ""}
              label="Assessment Time"
              variant="outlined"
              placeholder="Time duration for this assessment in mins"
              fullWidth
              style={{ marginBottom: 24 }}
            />

            <Box display="flex" justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                style={{ marginTop: 32, minWidth: 180 }}
                loading={isSubmitting}
              >
                SUBMIT
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </PageLayout>
  );
};

export default AssessmentForm;
